/**
 * AgentTranscript Component
 * 
 * Displays agent-only transcript with bottom-aligned text
 * that fades to transparent as older messages scroll up.
 * Uses CSS gradient mask for smooth fade effect.
 * Includes text buffering to slow down display to match speech.
 * Has a simple composer at the bottom for connecting and typing.
 */

import { useRef, useEffect, useMemo, useState } from 'react';
import { Send, Sparkles, ListTodo, FileText, Youtube, Brain, Eye, ChevronRight, Mic, MicOff, Lightbulb, Loader2, NotebookPen, Plus, X, Globe, Zap, ArrowUp } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { GeminiAudioManager } from '@/lib/gemini-audio';
import StudibudiFace from '@/components/brand/StudibudiFace';

export interface TranscriptMessage {
    id: string;
    content: string;
    type: 'user' | 'agent';
    timestamp: string;
    streaming?: boolean;
    source?: 'voice' | 'text';
    confidence?: number;
}

type AgentStatus = 'disconnected' | 'connecting' | 'authenticating' | 'connected' | 'ending' | 'error';

interface AgentTranscriptProps {
    messages: TranscriptMessage[];
    isAgentConnected: boolean;
    agentStatus?: AgentStatus;
    isUserMuted?: boolean;
    isUserSpeaking?: boolean;
    isAgentSpeaking?: boolean;
    onConnect?: () => void;
    onDisconnect?: () => void;
    onToggleMute?: () => void;
    onCollapse?: () => void;
    onSendText?: (text: string) => void;
    onAddAsNote?: (text: string) => void;
    audioManager?: GeminiAudioManager | null;
    // Infinite sessions: Show extending indicator
    isExtendingSession?: boolean;
    // Onboarding mode for new learners
    isOnboardingMode?: boolean;
}

// Visual typing config
const TYPING_SPEED_FAST = 22; // chars/sec when audio is loud (approx 225 wpm)
const TYPING_SPEED_SLOW = 2;  // chars/sec when audio is quiet (almost paused)
const SILENCE_TIMEOUT_MS = 1000; // Snap to end after 1s of silence if not streaming
const USER_TRANSCRIPT_MIN_WORDS = 4;
const USER_TRANSCRIPT_MIN_CHARS = 20;

type ActivationTone = 'amber' | 'emerald' | 'red' | 'cyan' | 'slate';

interface ActivationPrompt {
    title: string;
    example: string;
    icon: LucideIcon;
    tone: ActivationTone;
}

const ACTIVATION_PROMPTS: ActivationPrompt[] = [
    {
        title: 'Add to my todo list',
        example: 'Add "Learn backprop" to my todo list',
        icon: ListTodo,
        tone: 'emerald',
    },
    {
        title: 'Create a note...',
        example: 'Create a note about the Krebs cycle',
        icon: FileText,
        tone: 'amber',
    },
    {
        title: 'Find a YouTube video...',
        example: 'Find a YouTube video about linear regression',
        icon: Youtube,
        tone: 'red',
    },
    {
        title: 'Help me understand...',
        example: 'Help me understand momentum',
        icon: Lightbulb,
        tone: 'slate',
    },
    {
        title: 'Quiz me on a topic...',
        example: 'Quiz me on photosynthesis',
        icon: Brain,
        tone: 'emerald',
    },
    {
        title: 'Build a quick explainer...',
        example: 'Create a 5-minute explainer on momentum',
        icon: Sparkles,
        tone: 'amber',
    },
    {
        title: 'Explain "This"...',
        example: 'Explain "this" section of my journal',
        icon: Eye,
        tone: 'cyan',
    },
];

const ACTIVATION_TONE_STYLES: Record<ActivationTone, string> = {
    amber: 'text-amber-600',
    emerald: 'text-emerald-600',
    red: 'text-red-500',
    cyan: 'text-cyan-600',
    slate: 'text-slate-500',
};

interface TranscriptInputProps {
    value: string;
    setValue: (val: string) => void;
    onSend: () => void;
    isConnected: boolean;
    isConnecting: boolean;
    isUserMuted: boolean;
    onConnect?: () => void;
    onDisconnect?: () => void;
    onToggleMute?: () => void;
    isPendingConnection?: boolean;
}

const TranscriptInput = ({
    value,
    setValue,
    onSend,
    isConnected,
    isConnecting,
    isUserMuted,
    onConnect,
    onDisconnect,
    onToggleMute,
    isPendingConnection = false,
}: TranscriptInputProps) => {
    const hasValue = value.trim().length > 0;
    const isSendDisabled = (!hasValue && !isPendingConnection) || (!isConnected && !onConnect && !isPendingConnection);
    // Allow send if we have text (will auto-connect) OR if we are already connected/connecting
    const isSendActive = hasValue || isPendingConnection;

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (isSendDisabled || isPendingConnection) return;
            onSend();
        }
    };

    const handlePrimaryAction = () => {
        if (isSendDisabled || isPendingConnection) return;
        onSend();
    };

    const handleConnectToggle = () => {
        if (isConnecting) return;
        if (isConnected) {
            onDisconnect?.();
        } else {
            onConnect?.();
        }
    };

    return (
        <div className="relative rounded-[22px] bg-[#fcfbf9] p-2 border border-slate-200/60 transition-all focus-within:border-slate-300/80 focus-within:ring-1 focus-within:ring-slate-100 shadow-[0_2px_8px_-4px_rgba(0,0,0,0.02)]">
            <textarea
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isConnected ? 'Ask anything...' : 'Ask Studibudi...'}
                rows={1}
                className="w-full px-3 py-2 text-[13px] leading-relaxed rounded-none border-none bg-transparent text-[#37352f] placeholder:text-slate-400 focus:outline-none focus:ring-0 resize-none overflow-y-auto min-h-[44px] max-h-[160px]"
                style={{ height: 'auto' }}
                onInput={(e) => {
                    const target = e.target as HTMLTextAreaElement;
                    target.style.height = 'auto';
                    target.style.height = `${Math.min(target.scrollHeight, 160)}px`;
                }}
            />

            <div className="flex items-center justify-between px-1 pb-0.5 mt-0.5">
                {/* LEFT: Voice Controls (Hero / Capsule) */}
                <div className="flex items-center">
                    {!isConnected ? (
                        <button
                            onClick={onConnect}
                            disabled={isConnecting}
                            className={`
                                h-8 pl-4 pr-4 rounded-full bg-slate-700 text-white text-[12px] font-medium transition-all hover:bg-slate-800 hover:scale-[1.02] active:scale-95 flex items-center gap-2
                                ${isConnecting ? 'opacity-80 cursor-wait' : ''}
                            `}
                        >
                            {isConnecting ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin text-white/50" />
                            ) : null}
                            <span>{isConnecting ? 'Connecting...' : 'Start Studibudi'}</span>
                        </button>
                    ) : (
                        <div className="flex items-center rounded-full border border-slate-200/80 p-0.5 gap-0.5 transition-all hover:border-slate-300 shadow-[0_1px_2px_rgba(0,0,0,0.02)]">
                            {/* Merged Status & Mute Toggle */}
                            <button
                                onClick={onToggleMute}
                                className={`
                                    h-7 rounded-full flex items-center justify-start px-2.5 gap-2 transition-all border border-transparent
                                    ${isUserMuted
                                        ? 'text-rose-400 hover:bg-rose-50'
                                        : 'text-emerald-700 hover:bg-emerald-50'}
                                `}
                                title={isUserMuted ? "Tap to unmute" : "Tap to mute"}
                            >
                                {isUserMuted ? (
                                    <MicOff className="h-3.5 w-3.5 shrink-0" />
                                ) : (
                                    <div className="relative">
                                        <div className="absolute inset-0 bg-emerald-500 rounded-full animate-ping opacity-20" />
                                        <Mic className="h-3.5 w-3.5 shrink-0 relative z-10" />
                                    </div>
                                )}
                                <span className="text-[11px] font-medium truncate max-w-[60px]">
                                    {isUserMuted ? 'Mic off' : 'Listening'}
                                </span>
                            </button>

                            {/* Divider */}
                            <div className="w-px h-3 bg-slate-200 mx-0" />

                            {/* Disconnect X */}
                            <button
                                onClick={onDisconnect}
                                className="h-7 w-7 rounded-full flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                                title="End session"
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    )}
                </div>

                {/* RIGHT: Send Button */}
                <button
                    onClick={handlePrimaryAction}
                    disabled={isSendDisabled || isPendingConnection}
                    className={`
                        h-8 w-8 rounded-full flex items-center justify-center transition-all
                        ${isSendActive
                            ? 'bg-[#18181b] text-white hover:bg-[#27272a] hover:scale-105 active:scale-95'
                            : 'bg-slate-100 text-slate-300 cursor-not-allowed'}
                    `}
                    aria-label={isPendingConnection ? 'Connecting...' : 'Send message'}
                >
                    {isPendingConnection ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />
                    ) : (
                        <ArrowUp className="h-4 w-4" />
                    )}
                </button>
            </div>
        </div>
    );
};

interface AgentStatusBarProps {
    isConnected: boolean;
    isConnecting: boolean;
    isUserMuted: boolean;
    onConnect?: () => void;
    onDisconnect?: () => void;
    onToggleMute?: () => void;
}

const AgentStatusBar = ({
    isConnected,
    isConnecting,
    isUserMuted,
    onConnect,
    onDisconnect,
    onToggleMute,
}: AgentStatusBarProps) => {
    // Legacy component kept for safety but unused in new UI
    return null;
};

const PRIMARY_PROMPTS = ACTIVATION_PROMPTS.slice(0, 4);

const PromptRow = ({ prompt, onSelect }: { prompt: ActivationPrompt; onSelect: (example: string) => void }) => {
    const Icon = prompt.icon;
    return (
        <button
            type="button"
            onClick={() => onSelect(prompt.example)}
            className="w-full text-left rounded-md px-1 py-1.5 transition-colors hover:bg-slate-100 group"
        >
            <div className="flex items-center gap-3">
                <div className={`h-6 w-6 rounded flex items-center justify-center text-slate-400 group-hover:text-slate-600 transition-colors`}>
                    <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <p className="text-[13px] text-[#37352f]">{prompt.title}</p>
                    </div>
                </div>
            </div>
        </button>
    );
};

interface EmptyStateProps {
    onSelect: (text: string) => void;
    isOnboardingMode?: boolean;
}

const EmptyState = ({ onSelect, isOnboardingMode = false }: EmptyStateProps) => {
    const [showAll, setShowAll] = useState(false);
    const promptList = showAll ? ACTIVATION_PROMPTS : PRIMARY_PROMPTS;

    // Time-based contextual greeting (or welcome for onboarding)
    const getGreeting = () => {
        // Welcome greetings for new learners
        if (isOnboardingMode) {
            const welcomeGreetings = [
                "Hey! I'm Studibudi 👋",
                "Welcome! Let's learn together",
                "Hi there! Ready to explore?",
                "Nice to meet you!",
                "Hello! I'm here to help you learn",
            ];
            return welcomeGreetings[Math.floor(Math.random() * welcomeGreetings.length)];
        }

        const hour = new Date().getHours();
        const greetings = {
            morning: [
                "Good morning! Ready to learn?",
                "Rise and shine! Let's study",
                "Morning brain boost time!",
            ],
            afternoon: [
                "Let's tackle something new",
                "Afternoon learning mode",
                "Ready when you are!",
            ],
            evening: [
                "Evening study session?",
                "Winding down with some learning",
                "Night owl study time",
            ],
            night: [
                "Late night learning?",
                "Burning the midnight oil",
                "Still going strong!",
            ],
        };

        let timeSlot: keyof typeof greetings;
        if (hour >= 5 && hour < 12) timeSlot = 'morning';
        else if (hour >= 12 && hour < 17) timeSlot = 'afternoon';
        else if (hour >= 17 && hour < 21) timeSlot = 'evening';
        else timeSlot = 'night';

        const options = greetings[timeSlot];
        return options[Math.floor(Math.random() * options.length)];
    };

    // Use useMemo so greeting updates when isOnboardingMode changes
    const greeting = useMemo(getGreeting, [isOnboardingMode]);

    return (
        <div className="px-6 pt-10 pb-4">
            <div className="flex flex-col gap-2 mb-8">
                <div className="h-16 w-16 flex items-center justify-center mb-2">
                    <StudibudiFace className="h-14 w-11" />
                </div>
                <p className="text-xl font-semibold text-[#37352f] tracking-tight">{greeting}</p>
                <p className="text-sm text-[#787774]">Here are a few things I can do, or ask me anything!</p>
            </div>
            <div className="space-y-0.5">
                {promptList.map((prompt) => (
                    <PromptRow key={prompt.title} prompt={prompt} onSelect={onSelect} />
                ))}
            </div>
            <div className="mt-4 flex items-center justify-between">
                <button
                    type="button"
                    onClick={() => setShowAll((prev) => !prev)}
                    className="text-xs text-slate-400 hover:text-slate-600 px-2"
                >
                    {showAll ? 'Show fewer ideas' : 'Show more ideas'}
                </button>
            </div>
        </div>
    );
};

interface TranscriptHeaderProps {
    isAgentConnected: boolean;
    onCollapse?: () => void;
}

const TranscriptHeader = ({ isAgentConnected, onCollapse }: TranscriptHeaderProps) => (
    <div className="px-5 pt-5 pb-3 bg-transparent">
        <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-3">
                <StudibudiFace className="h-8 w-6" isActive={isAgentConnected} />
                <div className="flex flex-col">
                    <p className="text-[15px] font-semibold text-[#37352f] leading-snug">Studibudi</p>
                    <p className={`text-[13px] leading-snug flex items-center gap-1.5 ${isAgentConnected ? 'text-emerald-600' : 'text-[#787774]'}`}>
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${isAgentConnected ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                        {isAgentConnected ? 'Connected' : 'Disconnected'}
                    </p>
                </div>
            </div>

            {onCollapse && (
                <button
                    type="button"
                    onClick={onCollapse}
                    className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center text-slate-400 transition-colors hover:text-slate-600"
                    aria-label="Collapse panel"
                >
                    <ChevronRight className="h-4 w-4" />
                </button>
            )}
        </div>
    </div>
);


export function AgentTranscript({
    messages,
    isAgentConnected,
    agentStatus,
    isUserMuted = true,
    isUserSpeaking: _isUserSpeaking = false,
    isAgentSpeaking: _isAgentSpeaking = false,
    onConnect,
    onDisconnect,
    onToggleMute,
    onCollapse,
    onSendText,
    onAddAsNote,
    audioManager,
    isExtendingSession = false,
    isOnboardingMode = false,
}: AgentTranscriptProps) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const bottomRef = useRef<HTMLDivElement>(null);

    // Buffered text state for the currently streaming message
    const [bufferedContent, setBufferedContent] = useState<Record<string, string>>({});
    const bufferedLengthRef = useRef<Record<string, number>>({});
    const lastActivityRef = useRef<number>(Date.now());

    // Text input state
    const [inputValue, setInputValue] = useState('');
    const [pendingMessages, setPendingMessages] = useState<string[]>([]);
    const connectionStartTimeRef = useRef<number | null>(null);

    // Filter to only agent messages
    const agentMessages = useMemo(() =>
        messages.filter(m => m.type === 'agent'),
        [messages]
    );

    // Buffer text reveal for streaming messages
    useEffect(() => {
        // 1. Identify valid targets for typing animation
        // Messages that are not fully revealed yet
        const incompleteMsgs = agentMessages.filter(msg => {
            const currentLen = bufferedLengthRef.current[msg.id] || 0;
            return currentLen < msg.content.length;
        });

        // 2. Handle History Loading (Snap immediately)
        // If a message is NOT streaming and we haven't started typing it (len=0), 
        // it's likely history. Snap it immediately.
        incompleteMsgs.forEach(msg => {
            const currentLen = bufferedLengthRef.current[msg.id] || 0;
            if (!msg.streaming && currentLen === 0) {
                // Snap history
                bufferedLengthRef.current[msg.id] = msg.content.length;
                setBufferedContent(prev => ({
                    ...prev,
                    [msg.id]: msg.content
                }));
            }
        });

        // 3. Animate remaining incomplete messages (active typing)
        // Re-filter to get only those that need animation
        const activeTypingMsgs = incompleteMsgs.filter(msg => {
            const currentLen = bufferedLengthRef.current[msg.id] || 0;
            // Only animate if we didn't just snap it
            return currentLen < msg.content.length;
        });

        if (activeTypingMsgs.length === 0) return;

        // Determine typing speed based on Audio
        let charRate = TYPING_SPEED_SLOW;
        let hasAudioActivity = false;

        if (audioManager?.outputAnalyser) {
            const dataArray = new Uint8Array(audioManager.outputAnalyser.frequencyBinCount);
            audioManager.outputAnalyser.getByteFrequencyData(dataArray);

            // Check for volume
            let sum = 0;
            const checkCount = Math.min(dataArray.length, 50);
            for (let i = 0; i < checkCount; i++) sum += dataArray[i];
            const avg = sum / checkCount;

            if (avg > 10) {
                charRate = TYPING_SPEED_FAST;
                hasAudioActivity = true;
                lastActivityRef.current = Date.now();
            } else {
                // If silent, check how long
                const timeSinceAudio = Date.now() - lastActivityRef.current;

                // If silent for too long AND message is not streaming from backend, snap it
                if (timeSinceAudio > SILENCE_TIMEOUT_MS) {
                    activeTypingMsgs.forEach(msg => {
                        if (!msg.streaming) {
                            // Snap inactive messages
                            bufferedLengthRef.current[msg.id] = msg.content.length;
                            setBufferedContent(prev => ({
                                ...prev,
                                [msg.id]: msg.content
                            }));
                        }
                    });
                    return; // Skip scheduling next frame for these
                }
            }
        } else {
            // Fallback if no audio manager (e.g. muted or not connected yet? but connected is checked)
            // Just use a default decent speed
            charRate = 30;
        }

        // Schedule next character
        const msPerChar = 1000 / charRate;
        const timer = setTimeout(() => {
            setBufferedContent(prev => {
                const nextState = { ...prev };
                let updated = false;

                activeTypingMsgs.forEach(msg => {
                    const currentLen = bufferedLengthRef.current[msg.id] || 0;
                    if (currentLen < msg.content.length) {
                        const nextLen = currentLen + 1;
                        bufferedLengthRef.current[msg.id] = nextLen;
                        nextState[msg.id] = msg.content.slice(0, nextLen);
                        updated = true;
                    }
                });

                return updated ? nextState : prev;
            });
        }, msPerChar);

        return () => clearTimeout(timer);
    }, [agentMessages, bufferedContent, audioManager]);

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        if (bottomRef.current) {
            bottomRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [bufferedContent, messages.length]);

    // Get display content for a message (buffered or full)
    const getDisplayContent = (msg: TranscriptMessage): string => {
        return bufferedContent[msg.id] || '';
    };

    const resolvedAgentStatus: AgentStatus = agentStatus ?? (isAgentConnected ? 'connected' : 'disconnected');
    const isConnecting = resolvedAgentStatus === 'connecting' || resolvedAgentStatus === 'authenticating';

    // Handle send text
    const handleSend = () => {
        const trimmed = inputValue.trim();
        if (!trimmed || !onSendText) return;

        if (isAgentConnected) {
            onSendText(trimmed);
            setInputValue('');
            return;
        }

        // Offline mode: Queue message and trigger connection
        setPendingMessages((prev) => [...prev, trimmed]);
        connectionStartTimeRef.current = Date.now();
        // Do NOT clear input yet - show pending state instead
        // setInputValue(''); // Kept in state for "optimistic" UI if desired, but here we want to "freeze" it or show loading
        // actually for UX, maybe better to clear it but show a spinner? 
        // User asked: "keep the message in the textbox with a Loading looking and feel"
        // So we do NOT clear input value here.

        if (!isConnecting) onConnect?.();
    };

    const handlePromptSelect = (example: string) => {
        setInputValue(example);
    };

    // Queue Flushing Logic: Wait for greeting
    useEffect(() => {
        if (!isAgentConnected || pendingMessages.length === 0 || !onSendText) return;

        // Check if we have received a FRESH agent message (greeting) since connection started
        // If connectionStartTimeRef is null (connected before), we can send immediately? 
        // No, if pendingMessages > 0, we must have just connected.

        const startTime = connectionStartTimeRef.current || 0;

        // Find if there is any agent message that arrived AFTER we requested connection
        const hasFreshGreeting = messages.some(m =>
            m.type === 'agent' &&
            new Date(m.timestamp).getTime() > startTime
        );

        // Also strictly, if we already have agent messages from before, we might just be reconnecting.
        // But the user specifically mentioned the "Standard greeting" issue.

        if (hasFreshGreeting) {
            // Greeting arrived! Flush queue.
            pendingMessages.forEach((message) => onSendText(message));
            setPendingMessages([]);
            setInputValue(''); // Clear input now
            connectionStartTimeRef.current = null;
        } else {
            // Fallback safety: If 5s passed and still no greeting, just send it (maybe they have greeting disabled?)
            // Using a timeout inside effect is tricky with dependencies. 
            // Let's us a simple check against current time? No, that requires re-render.
            // We'll trust the greeting for now, or maybe the user connects manually.
        }

    }, [isAgentConnected, pendingMessages, onSendText, messages]);

    // Safety fallback for stuck pending messages (e.g. if greeting never comes for some reason)
    useEffect(() => {
        if (pendingMessages.length > 0 && isAgentConnected) {
            const timer = setTimeout(() => {
                // Force flush if stuck for 5s
                if (pendingMessages.length > 0) {
                    pendingMessages.forEach((message) => onSendText!(message));
                    setPendingMessages([]);
                    setInputValue('');
                    connectionStartTimeRef.current = null;
                }
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [pendingMessages, isAgentConnected, onSendText]);

    const hasMessages = messages.length > 0;

    const shouldShowVoiceTranscript = (msg: TranscriptMessage) => {
        if (msg.type !== 'user' || msg.source !== 'voice') return true;
        if (msg.streaming) return false;
        const trimmed = msg.content.trim();
        if (!trimmed) return false;
        const wordCount = trimmed.split(/\s+/).filter(Boolean).length;
        const hasLength = trimmed.length >= USER_TRANSCRIPT_MIN_CHARS || wordCount >= USER_TRANSCRIPT_MIN_WORDS;
        const hasConfidence = typeof msg.confidence === 'number' && msg.confidence >= 0.6;
        return hasConfidence || hasLength;
    };

    return (
        <div className="h-full flex flex-col min-h-0">
            <TranscriptHeader isAgentConnected={isAgentConnected} onCollapse={onCollapse} />

            <div className="flex-1 min-h-0 flex flex-col">
                {hasMessages ? (
                    <div
                        ref={scrollRef}
                        className="flex-grow overflow-y-auto relative min-h-0"
                        style={{
                            scrollbarWidth: 'thin',
                            scrollbarColor: 'rgba(0,0,0,0.1) transparent',
                            maskImage: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,1) 15%, rgba(0,0,0,1) 100%)',
                            WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,1) 15%, rgba(0,0,0,1) 100%)',
                        }}
                    >
                        <div className="min-h-full flex flex-col">
                            <div className="flex-grow" />
                            <div className="flex flex-col gap-6 py-4 px-6">
                                {messages.map((msg) => {
                                    if (msg.type === 'agent') {
                                        const displayText = getDisplayContent(msg);
                                        if (!displayText) return null;

                                        return (
                                            <div
                                                key={msg.id}
                                                className="text-left group relative"
                                            >
                                                <div className="relative pr-8">
                                                    <p
                                                        className="text-[14px] leading-7 text-[#37352f]"
                                                        style={{
                                                            fontWeight: 400,
                                                            letterSpacing: '-0.01em'
                                                        }}
                                                    >
                                                        {displayText}
                                                        {msg.streaming && displayText.length < msg.content.length && (
                                                            <span className="inline-block w-0.5 h-4 bg-gray-400 ml-0.5 animate-pulse" />
                                                        )}
                                                    </p>

                                                    {/* Add as Note Button */}
                                                    {onAddAsNote && !msg.streaming && (
                                                        <button
                                                            onClick={() => onAddAsNote(msg.content)}
                                                            className="absolute -right-2 top-0 opacity-0 group-hover:opacity-100 transition-all duration-200 p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                                                            title="Add as note"
                                                        >
                                                            <NotebookPen className="h-4 w-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    }

                                    const isVoice = msg.source === 'voice';
                                    const showTranscript = shouldShowVoiceTranscript(msg);
                                    const transcriptText = showTranscript ? msg.content.trim() : '';

                                    if (!isVoice && !transcriptText) return null;

                                    return (
                                        <div
                                            key={msg.id}
                                            className="flex flex-col items-end gap-1 text-right"
                                        >
                                            <div className={`
                                                max-w-[85%] rounded-[12px] px-3 py-2 text-[14px] leading-relaxed 
                                                bg-[#f1f1ef]
                                                ${isVoice ? 'rounded-tr-sm text-[#37352f]' : 'rounded-tr-sm text-[#37352f]'}
                                            `}>
                                                <div className="flex items-start gap-2.5">
                                                    {isVoice && (
                                                        <div className="mt-0.5 flex-shrink-0 text-slate-400">
                                                            <Mic className="h-3.5 w-3.5" />
                                                        </div>
                                                    )}
                                                    <span>{msg.content}</span>
                                                </div>
                                                {msg.streaming && (
                                                    <div className="mt-2 flex gap-1 justify-end">
                                                        <span className="h-1 w-1 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: '0ms' }} />
                                                        <span className="h-1 w-1 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: '150ms' }} />
                                                        <span className="h-1 w-1 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: '300ms' }} />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}

                                <div ref={bottomRef} />

                                {isExtendingSession && (
                                    <div className="text-left py-2 animate-fade-in">
                                        <span className="inline-flex items-center gap-1.5 text-xs text-slate-400 italic">
                                            <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse" />
                                            Extending session...
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex-1" />
                )}

                {!hasMessages && (
                    <div className="flex-shrink-0">
                        <EmptyState onSelect={handlePromptSelect} isOnboardingMode={isOnboardingMode} />
                    </div>
                )}

                <div className="flex-shrink-0 px-4 pb-4 pt-2">
                    <TranscriptInput
                        value={inputValue}
                        setValue={setInputValue}
                        onSend={handleSend}
                        isConnected={isAgentConnected}
                        isConnecting={isConnecting}
                        isUserMuted={isUserMuted}
                        onConnect={onConnect}
                        onDisconnect={onDisconnect}
                        onToggleMute={onToggleMute}
                        isPendingConnection={pendingMessages.length > 0}
                    />
                </div>
            </div>
        </div>
    );
}

export default AgentTranscript;
export { TranscriptInput };
